import React, { JSX, useContext, useEffect, useState, useRef, memo } from "react";

import { GlobalContext } from "@/contexts/GlobalContext";
import { CartContext } from "@/contexts/CartContext";
import { ModalContext } from "@/contexts/ModalsContext";

import ErrorModal from "@/modals/Error.modal";

import useFetch from "@/hooks/useFetchData";
import useGA from "@/hooks/useGA";

import { Cart } from "@/interfaces/SharedInterfaces";
import { FetchOptions } from "@/types/HTTP.types";

import styles from "@/styles/cart.module.scss";

interface CartStatus {
    isAdding?: boolean;
    isUpdating?: boolean;
    isRemoving?: boolean;
};

interface CartControlProps {
    partno: string | null;
    enabled?: boolean;
    stocked?: boolean;
    remove?: boolean;
};

const CartButtton = ({ partno, enabled = false, stocked, remove }: CartControlProps) => {
    const { globalState: { apiUrl } } = useContext(GlobalContext);
    const { cartState: { cartId, cart }, dispatchCart } = useContext(CartContext);
    const { dispatchModal } = useContext(ModalContext);
    const { eventGA } = useGA();
    const { useFetchData } = useFetch();

    const [cartStatus, setCartStatus] = useState<CartStatus>({ isAdding: false, isUpdating: false, isRemoving: false });
    const [method, setMethod] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const isMounted = useRef<boolean>(false);

    useEffect(() => {
        isMounted.current = false;

        if (cartStatus.isAdding) {
            addProdToCart();
        }

        return () => {
            isMounted.current = true;
        }
    }, [cartStatus.isAdding]);

    useEffect(() => {
        isMounted.current = false;

        if (cartStatus.isUpdating && method && cartId) {
            updateItemQuantity(cartId);
        }

        return () => {
            isMounted.current = true;
        }
    }, [cartStatus.isUpdating, method, cartId]);

    useEffect(() => {
        isMounted.current = false;

        if (cartStatus.isRemoving && cartId) {
            removeCartItem(cartId);
        }

        return () => {
            isMounted.current = true;
        }
    }, [cartStatus.isRemoving, cartId]);

    useEffect(() => {
        if (remove && cartId) {
            removeCartItem(cartId);
        }
    }, [remove, cartId]);

    /* ------------------------------------------------ API FUNCTIONS ------------------------------------------------ */

    const addProdToCart = async () => {
        const url: string = `${apiUrl}/cart/?partNo=${partno}${cartId ? `&ucid=${cartId}` : ""}`;
        const options: FetchOptions = {
            method: cartId ? "PUT" : "POST",
            body: JSON.stringify({ cartId: cartId || null, partNo: partno })
        };

        try {
            const result = await useFetchData<Cart>(url, options);

            if (!result.success) {
                setError("This item cannot be added to basket");
                dispatchModal({ type: "TOGGLE_ERROR" });
                return false;
            }

            dispatchCart({ type: "SET_CART_ID", cartId: result.data.cart_id });
            localStorage.setItem("_ucid", result.data.cart_id);

            if (!isMounted.current) {
                setCartStatus({ ...cartStatus, isAdding: false });
                const cart = calculateQuantity(result.data);
                dispatchCart({ type: "SET_CART", cart: cart });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const updateItemQuantity = async (cartId: string) => {
        const url: string = `${apiUrl}/cart/${cartId}/quantity`;
        const options: FetchOptions = {
            method: "PUT",
            body: JSON.stringify({ partNo: partno, method: method })
        };

        try {
            const result = await useFetchData<Cart>(url, options);

            if (!result.success) {
                return false;
            }

            if (!isMounted.current) {
                setMethod(null);
                setCartStatus({ ...cartStatus, isUpdating: false });
                const resCart = calculateQuantity(result.data);
                dispatchCart({ type: "SET_CART", cart: resCart });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const removeCartItem = async (cartId: string) => {
        const url: string = `${apiUrl}/cart/${cartId}/${partno}`;
        const options: FetchOptions = {
            method: "DELETE"
        };

        try {
            const result = await useFetchData<Cart>(url, options);

            if (!result.success) {
                return false;
            }

            if (!isMounted.current) {
                setCartStatus({ ...cartStatus, isRemoving: false });
                const resCart = calculateQuantity(result.data);
                dispatchCart({ type: "SET_CART", cart: resCart });
            }
        } catch (error) {
            console.error(error);
        }
    };

    /* ----------------------------------------- HANDLERS/FUNCTIONS/ELEMENTS ----------------------------------------- */

    const calculateQuantity = (list: Cart): Cart => {
        list.cart_items.forEach(ci => {
            ci.total = ci.price * ci.qty;
        });

        return list;
    };

    const handleAddToCart = (_: React.MouseEvent<HTMLButtonElement>) => {
        setCartStatus({ ...cartStatus, isAdding: true});

        eventGA({
            action: "AddToCart",
            params: {
                event_category: "Cart Button",
                event_label: "Add item to cart"
            }
        });
    };

    const handleUpdateQty = (event: React.MouseEvent<HTMLButtonElement>) => {
        const { name } = event.currentTarget;

        if (name === "none") {
            return false;
        }

        eventGA({
            action: name === "decrement" ? "DecrementItemCount" : "IncrementItemCount",
            params: {
                event_category: "Cart Button",
                event_label: name === "decrement" ? "Decrease Item Count" : "Increase Item Count"
            }
        });

        setCartStatus({ ...cartStatus, isUpdating: true });
        setMethod(name);
    };

    const handleRemoveItem = () => {
        eventGA({
            action: "RemoveFromCart",
            params: {
                event_category: "Cart Button",
                event_label: "Remove item from cart"
            }
        });
        
        setCartStatus({ ...cartStatus, isRemoving: true });
    };

    // const notAddedButton = <button className={styles["cart-btn--disabled"]}>N/A</button>;

    const notAddedButton = enabled && stocked ? (
        <button 
            data-partno={partno} 
            disabled={cartStatus.isAdding} 
            onClick={handleAddToCart} 
            className={styles[`cart-btn${cartStatus.isAdding ? "--disabled" : ""}`]}
        >Add to Basket</button>
    ) : <button className={styles["cart-btn--disabled"]}>Out of Stock</button>;

    // const addedButton = <button className={styles["cart-btn--disabled"]}>N/A</button>;

    const addedButton = (encSku?: string, qty?: number) => {
        return (
            <div className={styles["in-cart"]}>
                <div className={styles["in-cart-buttons"]}>
                    {qty === 1 ?
                        <button
                            data-partno={encSku} 
                            disabled={cartStatus.isRemoving} 
                            onClick={handleRemoveItem} 
                            className={styles["delete-btn"]}
                        >
                            <i className="fa fa-trash-o" aria-hidden="true"></i>
                        </button> :
                        <button 
                            disabled={cartStatus.isUpdating || !stocked || qty === 1} 
                            onClick={handleUpdateQty} 
                            className={styles["in-cart-btn"]} 
                            name="decrement"
                        >
                            <i className="fa fa-minus" aria-hidden="true"></i>
                        </button>}

                    <input className={styles["in-cart-qty"]} type="text" readOnly value={qty || ""} />
                    <button 
                        disabled={cartStatus.isUpdating || !stocked} 
                        onClick={handleUpdateQty} 
                        className={styles["in-cart-btn"]} 
                        name="increment"
                    >
                        <i className="fa fa-plus" aria-hidden="true"></i>
                    </button>
                </div>

                {/* <button  */}
                {/*     data-partno={encSku}  */}
                {/*     disabled={cartStatus.isRemoving}  */}
                {/*     onClick={handleRemoveItem}  */}
                {/*     className={styles["cart-btn--remove"]} */}
                {/* >Remove</button> */}
            </div>
        );
    };

    const renderButton = (cart: Cart): JSX.Element => {
        let node: JSX.Element = notAddedButton;

        for (const item of cart.cart_items) {
            if (item.ref_sku !== partno) {
                continue;
            }

            node = addedButton(item.ref_sku, item.qty);
            // node = addedButton;
            break;
        }

        return node;
    };

    return (
        <React.Fragment>
            {error && <ErrorModal errMsg={error} />}
            {cart ? renderButton(cart) : notAddedButton}
        </React.Fragment>
    );
};

export default memo(CartButtton);
